import { AnimatePresence, motion } from 'framer-motion';
import {
  BookOpen,
  Brain,
  ChevronDown,
  Circle,
  Flame,
  HelpCircle,
  Lock,
  Target,
  Trophy,
  Zap,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { usePesaParcours } from '../../stores/usePesaParcours';

const SidebarNavigation = ({ isCollapsed }) => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const {
    modules,
    activeInvestigation,
    focusResource,
    currentModuleIndex,
    openDiagnosticQuiz,
    openFinalQuiz,
    courseData,
    ui,
    activeItem,
  } = usePesaParcours();
  const { sidebarExpanded } = ui;

  const navigate = useNavigate();
  const [expandedModule, setExpandedModule] = useState(null);
  const [expandedTheory, setExpandedTheory] = useState({});
  const [expandedPractice, setExpandedPractice] = useState({});

  const sortedModules = [...(modules || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const globalCaseSlug =
    courseData?.caseId ||
    modules?.[0]?.cases?.[0]?.slug ||
    modules?.[0]?.cases?.[0]?.id;

  useEffect(() => {
    if (sidebarExpanded && currentModuleIndex !== undefined) {
      setExpandedModule(currentModuleIndex);
      setExpandedTheory((prev) => ({ ...prev, [currentModuleIndex]: true }));
      setExpandedPractice((prev) => ({ ...prev, [currentModuleIndex]: false }));
    }
  }, [sidebarExpanded, currentModuleIndex]);

  const getSafeId = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return item;
    return item.id || item._id?.toString() || item.slug;
  };

  const isActive = (id) => {
    const activeId = getSafeId(activeInvestigation);
    return activeId === id || activeItem?.id === id;
  };

  const toggleTheory = (mIdx) =>
    setExpandedTheory((prev) => ({ ...prev, [mIdx]: !prev[mIdx] }));
  const togglePractice = (mIdx) =>
    setExpandedPractice((prev) => ({ ...prev, [mIdx]: !prev[mIdx] }));

  // Verrouillage des modules 2 et 3 tant que le module 1 n’a pas 70 %
  const isModuleLocked = (index) => {
    if (isSuperAdmin) return false;
    if (index === 0) return false;
    const firstModuleCompletion = modules?.[0]?.completion || 0;
    return firstModuleCompletion < 70;
  };

  return (
    <div className="flex flex-col h-full p-2">
      <div className="flex-1 overflow-y-auto no-scrollbar space-y-1.5">
        {/* ---- DIAGNOSTIC INITIAL ---- */}
        {!isCollapsed && modules?.[0]?.diagnosticQuiz && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            onClick={() => openDiagnosticQuiz(modules[0].id)}
            className={`w-full flex items-center gap-2 p-2 rounded-xl transition-all border ${
              darkMode
                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'
                : 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            <Brain size={12} />
            <span className="text-[9px] font-black uppercase tracking-widest">
              Diagnostic Initial
            </span>
          </motion.button>
        )}

        {/* ---- MODULES + INTERCALAIRES ---- */}
        {sortedModules.map((module, mIdx) => {
          const isCurrent = mIdx === currentModuleIndex;
          const isExpanded = expandedModule === mIdx;
          const isLastModule = mIdx === sortedModules.length - 1;
          const hasFinalQuiz = !!(
            module.finalQuiz ||
            module.quizId ||
            (module.quiz && module.quiz.length > 0)
          );
          const locked = isModuleLocked(mIdx);

          const theoryGranules =
            module.granules?.filter(
              (g) => g.type !== 'activity' && g.type !== 'capstone'
            ) || [];
          const activityGranules =
            module.granules?.filter((g) => g.type === 'activity') || [];
          const capstoneGranules =
            module.granules?.filter((g) => g.type === 'capstone') || [];

          return (
            <React.Fragment key={getSafeId(module) || mIdx}>
              {/* HEADER DU MODULE */}
              <motion.button
                whileHover={locked ? {} : { scale: 1.01 }}
                onClick={() => {
                  if (locked) return;
                  setExpandedModule(isExpanded ? null : mIdx);
                }}
                disabled={locked}
                className={`w-full flex items-center gap-2 p-2 rounded-xl transition-all border ${
                  locked
                    ? 'opacity-40 cursor-not-allowed'
                    : isCurrent
                    ? darkMode
                      ? 'bg-blue-500/10 border-blue-500/20'
                      : 'bg-blue-100/70 border-blue-300'
                    : darkMode
                    ? 'hover:bg-white/5 border-transparent'
                    : 'hover:bg-slate-50 border-transparent'
                }`}
              >
                <div
                  className={`shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-[9px] font-bold ${
                    locked
                      ? 'bg-slate-500/20 text-slate-400'
                      : isCurrent
                      ? 'bg-blue-500 text-white'
                      : darkMode
                      ? 'bg-slate-800 text-slate-500'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {locked ? <Lock size={12} /> : mIdx + 1}
                </div>

                {!isCollapsed && (
                  <div className="flex-1 text-left min-w-0">
                    <h3
                      className={`text-[9px] font-black uppercase tracking-tight truncate ${
                        locked
                          ? 'text-slate-400'
                          : isCurrent
                          ? darkMode
                            ? 'text-blue-400'
                            : 'text-blue-700'
                          : darkMode
                          ? 'text-slate-300'
                          : 'text-slate-700'
                      }`}
                    >
                      {module.title || module.titre}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div
                        className={`flex-1 h-1 ${
                          darkMode ? 'bg-white/5' : 'bg-slate-200'
                        } rounded-full overflow-hidden`}
                      >
                        <div
                          className={`h-full ${
                            locked
                              ? 'bg-slate-400'
                              : darkMode
                              ? 'bg-blue-500'
                              : 'bg-blue-600'
                          }`}
                          style={{ width: `${module.completion || 0}%` }}
                        />
                      </div>
                      <ChevronDown
                        size={10}
                        className={`text-slate-500 transition-transform ${
                          isExpanded && !locked ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>
                )}
              </motion.button>

              {/* CONTENU DU MODULE */}
              <AnimatePresence>
                {isExpanded && !isCollapsed && !locked && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="pl-5 space-y-1.5 overflow-hidden"
                  >
                    {/* SAVOIRS (Théorie - Bleu) */}
                    {theoryGranules.length > 0 && (
                      <div
                        className={`rounded-lg border ${
                          darkMode
                            ? 'border-white/5 bg-white/[0.02]'
                            : 'border-blue-100 bg-blue-50/30'
                        }`}
                      >
                        <button
                          onClick={() => toggleTheory(mIdx)}
                          className="w-full flex items-center justify-between p-1.5 hover:bg-white/5 transition-colors rounded-t-lg"
                        >
                          <div
                            className={`flex items-center gap-1.5 text-[8px] font-black uppercase tracking-widest ${
                              darkMode ? 'text-blue-400/70' : 'text-blue-600/80'
                            }`}
                          >
                            <BookOpen size={10} /> Support Théorique (
                            {theoryGranules.length})
                          </div>
                          <ChevronDown
                            size={10}
                            className={
                              expandedTheory[mIdx] ? 'rotate-180' : ''
                            }
                          />
                        </button>
                        {expandedTheory[mIdx] && (
                          <div className="space-y-0.5 p-1.5 pt-0">
                            {theoryGranules.map((g) => {
                              const gId = getSafeId(g);
                              return (
                                <button
                                  key={gId}
                                  onClick={() => focusResource(gId)}
                                  className={`w-full flex items-center gap-1.5 p-1.5 rounded-lg text-left text-[9px] uppercase tracking-wide transition-all ${
                                    isActive(gId)
                                      ? darkMode
                                        ? 'bg-blue-500/10 text-white'
                                        : 'bg-blue-100 text-blue-800'
                                      : darkMode
                                      ? 'text-slate-400 hover:text-slate-200'
                                      : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                                >
                                  <Circle
                                    size={isActive(gId) ? 5 : 4}
                                    fill={
                                      isActive(gId) ? 'currentColor' : 'none'
                                    }
                                  />
                                  {g.titre}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* PRATIQUE (Orange) */}
                    {activityGranules.length > 0 && (
                      <div
                        className={`rounded-lg border ${
                          darkMode
                            ? 'border-orange-500/20 bg-orange-500/5'
                            : 'border-orange-200 bg-orange-50/30'
                        }`}
                      >
                        <button
                          onClick={() => togglePractice(mIdx)}
                          className="w-full flex items-center justify-between p-1.5 hover:bg-white/5 transition-colors rounded-t-lg"
                        >
                          <div
                            className={`flex items-center gap-1.5 text-[8px] font-black uppercase tracking-widest ${
                              darkMode
                                ? 'text-orange-400/70'
                                : 'text-orange-600/80'
                            }`}
                          >
                            <Target size={10} /> Mise en Pratique (
                            {activityGranules.length})
                          </div>
                          <ChevronDown
                            size={10}
                            className={
                              expandedPractice[mIdx] ? 'rotate-180' : ''
                            }
                          />
                        </button>
                        {expandedPractice[mIdx] && (
                          <div className="space-y-0.5 p-1.5 pt-0">
                            {activityGranules.map((g) => {
                              const gId = getSafeId(g);
                              return (
                                <button
                                  key={gId}
                                  onClick={() => focusResource(gId)}
                                  className={`w-full flex items-center gap-1.5 p-1.5 rounded-lg text-[9px] font-bold uppercase transition-all ${
                                    isActive(gId)
                                      ? darkMode
                                        ? 'bg-orange-500/10 border border-orange-500/30 text-orange-400'
                                        : 'bg-orange-50 border border-orange-300 text-orange-700'
                                      : darkMode
                                      ? 'text-slate-400 hover:bg-white/5'
                                      : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                                >
                                  <Zap
                                    size={10}
                                    className={
                                      isActive(gId) ? 'animate-pulse' : ''
                                    }
                                  />
                                  {g.titre}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* QUIZ FINAL DU MODULE (Violet) */}
                    {hasFinalQuiz && (
                      <button
                        onClick={() => openFinalQuiz(module.id)}
                        className={`w-full flex items-center gap-1.5 p-1.5 rounded-lg transition-all border ${
                          darkMode
                            ? 'text-purple-400 bg-purple-500/5 border-purple-500/20 hover:bg-purple-500/10'
                            : 'text-purple-700 bg-purple-50 border-purple-300 hover:bg-purple-100'
                        }`}
                      >
                        <HelpCircle size={10} />
                        <span className="text-[8px] font-black uppercase tracking-widest">
                          Évaluation Module {mIdx + 1}
                        </span>
                      </button>
                    )}

                    {/* CAPSTONE (Grande Finale) */}
                    {isLastModule &&
                      capstoneGranules.map((cap) => (
                        <motion.button
                          key={getSafeId(cap)}
                          whileHover={{ scale: 1.01 }}
                          onClick={() => focusResource(getSafeId(cap))}
                          className={`w-full flex flex-col gap-0.5 p-3 rounded-xl transition-all border relative overflow-hidden ${
                            darkMode
                              ? 'bg-gradient-to-br from-red-500/20 to-amber-500/10 border-red-500/30'
                              : 'bg-gradient-to-br from-red-50 to-amber-50 border-red-400/50'
                          }`}
                        >
                          <div className="flex items-center gap-1.5 relative z-10">
                            <Trophy size={14} className="text-amber-500" />
                            <span
                              className={`text-[9px] font-black uppercase tracking-widest ${
                                darkMode ? 'text-red-400' : 'text-red-700'
                              }`}
                            >
                              Grande Finale
                            </span>
                          </div>
                          <span
                            className={`text-[8px] font-medium text-left uppercase leading-tight ${
                              darkMode ? 'text-red-300/80' : 'text-red-800/80'
                            }`}
                          >
                            {cap.titre}
                          </span>
                        </motion.button>
                      ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* ---- INTERCALAIRES UNIQUES ---- */}
              {/* Centre d’ajustement après le module 1 */}
              {mIdx === 0 && !isCollapsed && (
                <button
                  onClick={() => navigate('/centre-ajustement')}
                  className={`w-full flex items-center gap-2 p-2 rounded-xl transition-all border ${
                    darkMode
                      ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                  }`}
                >
                  <Flame size={12} />
                  <span className="text-[9px] font-black uppercase tracking-widest">
                    Centre d’ajustement
                  </span>
                </button>
              )}

              {/* Simulateur après le module 2 */}
              {mIdx === 1 && globalCaseSlug && !isCollapsed && (
                <button
                  onClick={() =>
                    navigate(`/simulateur/case/${globalCaseSlug}`)
                  }
                  className={`w-full flex items-center gap-2 p-2 rounded-xl transition-all border ${
                    darkMode
                      ? 'bg-orange-500/5 border-orange-500/20 text-orange-400 hover:bg-orange-500/10'
                      : 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100'
                  }`}
                >
                  <Flame size={12} />
                  <span className="text-[9px] font-black uppercase tracking-widest">
                    Simulateur
                  </span>
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default SidebarNavigation;